import React, { useMemo, useState, useCallback } from 'react';
import { Button, Space, Tag, Tooltip } from 'antd';
import type { ColumnsType, ColumnType, TableProps } from 'antd/es/table';
import type { FilterValue } from 'antd/es/table/interface';

export type TableFiltersState = Record<string, FilterValue | null | undefined>;

type FilterBadge = {
  id: string;
  columnKey: string;
  value: React.Key;
  text: string;
};

const flattenColumns = <T,>(columns: ColumnsType<T>): ColumnType<T>[] => {
  const result: ColumnType<T>[] = [];
  columns.forEach((column) => {
    if ('children' in column && column.children) {
      result.push(...flattenColumns(column.children as ColumnsType<T>));
    } else {
      result.push(column as ColumnType<T>);
    }
  });
  return result;
};

const getColumnKey = <T,>(column: ColumnType<T>): string | null => {
  const rawKey = column.key ?? column.dataIndex;
  if (rawKey === undefined || rawKey === null) {
    return null;
  }
  if (Array.isArray(rawKey)) {
    return rawKey.map((part) => String(part)).join('.');
  }
  return String(rawKey);
};

const getColumnTitle = <T,>(column: ColumnType<T>, fallback: string): string => {
  const title = column.title;
  if (typeof title === 'string' || typeof title === 'number') {
    return String(title);
  }
  if (React.isValidElement(title)) {
    const child = title.props?.children;
    if (typeof child === 'string' || typeof child === 'number') {
      return String(child);
    }
  }
  return fallback;
};

const buildColumnLabelMap = <T,>(columns: ColumnsType<T>): Record<string, string> => {
  const map: Record<string, string> = {};
  flattenColumns(columns).forEach((column) => {
    const columnKey = getColumnKey(column);
    if (!columnKey) {
      return;
    }
    if (!map[columnKey]) {
      map[columnKey] = getColumnTitle(column, columnKey);
    }
  });
  return map;
};

const formatFilterValue = (value: React.Key): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const buildFilterBadges = <T,>(columns: ColumnsType<T>, filters: TableFiltersState): FilterBadge[] => {
  const labels = buildColumnLabelMap(columns);
  const badges: FilterBadge[] = [];

  Object.entries(filters).forEach(([columnKey, values]) => {
    if (values === null || values === undefined) {
      return;
    }
    const normalizedValues = Array.isArray(values) ? values : [values];
    const label = labels[columnKey] ?? columnKey;

    normalizedValues.forEach((value, index) => {
      const displayValue = formatFilterValue(value as React.Key);
      if (!displayValue) {
        return;
      }
      badges.push({
        id: `${columnKey}-${displayValue}-${index}`,
        columnKey,
        value: value as React.Key,
        text: `${label}: ${displayValue}`,
      });
    });
  });

  return badges;
};

export const applyFiltersToColumns = <T,>(
  columns: ColumnsType<T>,
  filters: TableFiltersState,
): ColumnsType<T> => {
  return columns.map((column) => {
    if ('children' in column && column.children) {
      return {
        ...column,
        children: applyFiltersToColumns(column.children as ColumnsType<T>, filters),
      };
    }

    const columnKey = getColumnKey(column as ColumnType<T>);
    if (!columnKey) {
      return column;
    }

    const hasFilter =
      Boolean((column as ColumnType<T>).onFilter) ||
      Boolean((column as ColumnType<T>).filterDropdown) ||
      Boolean((column as ColumnType<T>).filters);

    if (!hasFilter) {
      return column;
    }

    return {
      ...column,
      filteredValue: filters[columnKey] ?? null,
    };
  });
};

export const useTableFilters = <T,>() => {
  const [filters, setFilters] = useState<TableFiltersState>({});

  const handleTableChange: TableProps<T>['onChange'] = (_pagination, nextFilters) => {
    setFilters(nextFilters as TableFiltersState);
  };

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const removeFilterValue = useCallback((columnKey: string, value: React.Key) => {
    setFilters((prev) => {
      const current = prev[columnKey];
      if (!current) {
        return prev;
      }

      if (!Array.isArray(current)) {
        return {
          ...prev,
          [columnKey]: null,
        };
      }

      const nextValues = current.filter((item) => item !== value);
      return {
        ...prev,
        [columnKey]: nextValues.length > 0 ? nextValues : null,
      };
    });
  }, []);

  return { filters, setFilters, handleTableChange, clearAllFilters, removeFilterValue };
};

const TableFiltersBar: React.FC<{
  columns: ColumnsType<any>;
  filters: TableFiltersState;
  onRemove: (columnKey: string, value: React.Key) => void;
  onClearAll: () => void;
}> = ({ columns, filters, onRemove, onClearAll }) => {
  const badges = useMemo(() => buildFilterBadges(columns, filters), [columns, filters]);

  if (badges.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 12,
      }}
    >
      <span style={{ fontWeight: 500 }}>Filtros:</span>
      <Space size={[8, 8]} wrap>
        {badges.map((badge) => (
          <Tooltip key={badge.id} title={badge.text}>
            <Tag
              closable
              onClose={(event) => {
                event.preventDefault();
                onRemove(badge.columnKey, badge.value);
              }}
            >
              {badge.text}
            </Tag>
          </Tooltip>
        ))}
      </Space>
      <Button type="link" onClick={onClearAll}>
        Limpiar filtros
      </Button>
    </div>
  );
};

export default TableFiltersBar;
